import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { AlertTriangle, BarChart2, Eye, EyeOff, Lock, Mail, Unlock } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useMutation, useServerErrors } from '../../../Shared/api';
import { Editor } from '../../../Shared/Editor/Editor';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Project, User } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { Toggle } from '../../../Shared/UI/Toggle';
import useStickyElement from '../../../Shared/useStickyElement';
import { useRouter } from '../../router';
import { Charity, ProjectMailBodies, ProjectMailSubjects, ProjectMailType, ProjectMailVariables } from '../../types';
import MailEditor from './MailEditor';

export interface EditProjectMailFormProps {
  name: ProjectMailType;
  project: Project & ProjectMailSubjects & ProjectMailBodies & ProjectMailVariables & {
    new_campaign_sent_count: number;
    donation_received_sent_count: number;
    donation_confirmation_sent_count: number;
    reminder_sent_count: number;
    target_reached_sent_count: number;
    upcoming_event_sent_count: number;
    upcoming_event_reminder_sent_count: number;
  };
  charity: Charity;
  user: User;
}

export default function EditProjectMailForm({
  name,
  project,
  charity,
  user,
}: EditProjectMailFormProps) {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const subjectKey: keyof ProjectMailSubjects = `${name}_subject`;
  const bodyKey: keyof ProjectMailBodies = `${name}_mail`;
  const variables = project[`${name}_variables`];

  const form = useForm({
    defaultValues: {
      subject: project[subjectKey],
      body: project[bodyKey],
    },
  });

  const {
    register, control, handleSubmit, watch,
    formState: { defaultValues, errors, isDirty },
  } = form;

  const body = watch('body');

  const [editProject, { loading: saving, errors: serverErrors }] = useMutation(
    routes.edit_project_mails(project.id),
    {
      preserveState: 'errors',
      onSuccess: () => {
        notifier.notify(t('backend:mail_saved'));
      },
    }
  );

  useServerErrors(form, serverErrors);

  const enabledKey = `${name}_mail_enabled`;
  const canBeToggled = enabledKey in project;
  const enabled = canBeToggled ? !!project[enabledKey as keyof Project] : true;
  const isDraft = !enabled && isDirty;

  const [toggleMail, { loading: toggling }] = useMutation(
    routes.toggle_project_mail(project.id, name),
    {
      data: {
        enabled: !enabled,
      },
      preserveScroll: true,
      onSuccess: () => {
        notifier.notify(
          !enabled ? t('backend:mail_enabled') : t('backend:mail_disabled'),
          !enabled ? Eye : EyeOff
        );
      },
      onError: () => {
        notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
      },
    }
  );

  const stickyHeader = useStickyElement({
    position: 'top',
    offset: 60,
  });

  const loading = saving || toggling;

  const submit = async (data: typeof defaultValues) => {
    await editProject({
      [subjectKey]: data?.subject,
      [bodyKey]: data?.body,
      return: bodyKey.replace('_mail', ''),
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div className="!p-0" ref={stickyHeader.anchorRef}>
          <div
            ref={stickyHeader.elementRef}
            className={classNames(stickyHeader.isSticky && 'fixed z-20', 'max-w-screen-xl mx-auto px-8')}
            style={stickyHeader.isSticky ? stickyHeader.position : {}}
          >
            <div className={classNames('py-4', stickyHeader.isSticky && 'px-8 bg-gradient-to-t from-slate-50 to-white border-b')}>
              <div className="flex justify-between">
                <h3>
                  <Icon className="mr-1">
                    <Mail />
                  </Icon>
                  {t(`backend:${name}_mail`)}
                </h3>
                <div className="flex items-center space-x-6 text-sm">
                  <Link href={`${routes.mail_dashboard_page()}?project=${project.id}&mail=${name}`} className="text-slate-800">
                    <Icon className="mr-1">
                      <BarChart2 />
                    </Icon>
                    {t('backend:times_sent', { value: formatNumber(project[`${name}_sent_count`]) })}
                  </Link>
                  <div className="flex items-center space-x-4">
                    <span
                      className={classNames(
                        'font-medium',
                        enabled ? 'text-emerald-600' : 'text-slate-500',
                      )}>
                      {canBeToggled && (
                        <Icon className="mr-1">
                          {enabled ? <Lock /> : <Unlock />}
                        </Icon>
                      )}
                      {enabled ? t('backend:active') : (isDraft ? t('backend:draft') : t('backend:inactive'))}
                    </span>
                    {canBeToggled && (
                      <Toggle
                        checked={enabled}
                        onChange={() => toggleMail()}
                        disabled={isDraft || loading}
                        aria-label={enabled ? t('backend:disable_mail') : t('backend:enable_mail')}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div>
            <div className="text-slate-500">
              {t(`backend:${name}_mail_description`)}
            </div>
          </div>
          <div className="space-y-6">
            {serverErrors && (
              <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />
            )}
            <InputGroup>
              <InputLabel valid={!errors.subject} htmlFor="email_subject">
                {t('backend:subject')}
              </InputLabel>
              <Input
                {...register('subject')}
                id="email_subject"
                aria-invalid={!!errors.subject}
              />
              <ErrorMessage attribute={t('auth:login_code')} error={errors.subject }/>
            </InputGroup>
            <InputGroup>
              <InputLabel>
                {t('backend:body')}
              </InputLabel>
              <MailEditor
                type={name}
                editor={(
                  <Controller
                    control={control}
                    name="body"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <Editor
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        allowProjectColor
                        variables={variables}
                      />
                    )}
                  />
                )}
                preview={body}
                project={project}
                charity={charity}
                user={user}
              />
            </InputGroup>
          </div>
        </div>
        <div className="flex justify-end items-center space-x-6">
          <Button
            type="submit"
            loading={loading}
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}

